import { CarespaceRecordsProvider } from "./carespaceRecords";
import { CarespaceCountLink } from "./carespaceCountLink";
import { navigatorCarespacesUrl } from "./careNavigationUrls";
import { useQuery } from "@tanstack/react-query";
import type { CareNavigationMonthlyCompletionResponse } from "./careNavigationCriteria";
import { DataTable, KpiCard, KpiGrid, type DataTableColumn } from "./dashboardKit";
import { careNavigationMonthlyCompletionQueryOptions } from "./dashboardsReactQuery";
import { todayIsoInTimezone } from "./careNavigationShared";

type CompletionRow = CareNavigationMonthlyCompletionResponse["rows"][number];
const MONTHLY_COMPLETION_DEFINITION =
  "Did this aligned Carespace have any DCMP billing in the month?";
type SummaryRow = {
  id: string;
  label: string;
  alignedCount: number;
  completedCount: number;
  bookedRemainingCount: number | null;
  recordIds?: Record<string, string[]>;
};

const percentage = (row: Pick<SummaryRow, "alignedCount" | "completedCount">) =>
  row.alignedCount ? `${((row.completedCount / row.alignedCount) * 100).toFixed(1)}%` : "—";

export function summarizeMonthlyCompletion(
  rows: CompletionRow[],
  monthStart: string,
  navigators: readonly string[],
) {
  const filtered = rows.filter(
    (row) => !navigators.length || navigators.includes(row.navigatorName),
  );
  const months = new Map<string, SummaryRow>();
  for (const row of filtered) {
    const total = months.get(row.monthStart) ?? {
      id: row.monthStart,
      label: row.monthStart.slice(0, 7),
      alignedCount: 0,
      completedCount: 0,
      bookedRemainingCount: 0,
    };
    total.alignedCount += row.alignedCount;
    total.completedCount += row.completedCount;
    total.bookedRemainingCount =
      total.bookedRemainingCount === null || row.bookedRemainingCount === undefined
        ? null
        : total.bookedRemainingCount + row.bookedRemainingCount;
    total.recordIds = {
      aligned: [
        ...new Set([...(total.recordIds?.aligned ?? []), ...(row.recordIds?.aligned ?? [])]),
      ],
      bookedRemaining: [
        ...new Set([
          ...(total.recordIds?.bookedRemaining ?? []),
          ...(row.recordIds?.bookedRemaining ?? []),
        ]),
      ],
      completed: [
        ...new Set([...(total.recordIds?.completed ?? []), ...(row.recordIds?.completed ?? [])]),
      ],
    };
    months.set(row.monthStart, total);
  }
  return {
    current: months.get(monthStart) ?? {
      id: monthStart,
      label: monthStart,
      alignedCount: 0,
      completedCount: 0,
      bookedRemainingCount: null,
    },
    months: Array.from(months.values()),
    navigators: filtered
      .filter((row) => row.monthStart === monthStart)
      .map((row) => ({
        ...row,
        id: row.navigatorUserId,
        label: row.navigatorName,
        bookedRemainingCount: row.bookedRemainingCount ?? null,
      })),
  };
}

const BOOKED_DEFINITION =
  "Distinct aligned Carespaces without DCMP billing this month that have an active future enrollment, ongoing-care, needs-assessment or annual-assessment booking, or expected low-tier group attendance, before month end. Each Carespace counts once; cancelled, reassigned, past and next-month bookings are excluded.";

function projectedPercentage(row: SummaryRow) {
  return row.bookedRemainingCount === null
    ? "—"
    : percentage({
        alignedCount: row.alignedCount,
        completedCount: row.completedCount + row.bookedRemainingCount,
      });
}

function columns(
  breakdown: "Month" | "Navigator",
  showProjection = false,
): Array<DataTableColumn<SummaryRow>> {
  const result: Array<DataTableColumn<SummaryRow>> = [
    {
      key: "label",
      label: breakdown,
      sortable: true,
      definition:
        breakdown === "Month"
          ? "Billing month; the Carespaces current-month boundary is UTC."
          : "Currently assigned care navigator.",
    },
    {
      key: "completedCount",
      render: (row) => (
        <CarespaceCountLink
          count={row.completedCount}
          recordIds={row.recordIds?.completed}
          label={`${row.label} · CareSpaces with DCMP billing`}
          fallbackUrl={navigatorCarespacesUrl(row.id)}
        />
      ),
      label: "DCMP billed",
      align: "right",
      sortable: true,
      definition: MONTHLY_COMPLETION_DEFINITION,
    },
    {
      key: "alignedCount",
      render: (row) => (
        <CarespaceCountLink
          count={row.alignedCount}
          recordIds={row.recordIds?.aligned}
          label={`${row.label} · aligned CareSpaces`}
          fallbackUrl={navigatorCarespacesUrl(row.id)}
        />
      ),
      label: "Aligned",
      align: "right",
      sortable: true,
      definition:
        "CareSpaces aligned and non-archived at any point in this reporting month, with a patient and currently assigned care navigator, across non-software organizations.",
    },
    {
      key: "completion",
      label: "Billed completion",
      align: "right",
      sortable: true,
      definition:
        "Aligned Carespaces with any DCMP billing in the month divided by all aligned Carespaces.",
      sortValue: (row) => (row.alignedCount ? row.completedCount / row.alignedCount : null),
      render: percentage,
    },
  ];
  if (showProjection) {
    result.push(
      {
        key: "bookedRemainingCount",
        label: "Booked remaining",
        align: "right",
        sortable: true,
        definition: BOOKED_DEFINITION,
        render: (row) => (
          <CarespaceCountLink
            count={row.bookedRemainingCount}
            recordIds={row.recordIds?.bookedRemaining}
            label={`${row.label} · booked remaining`}
          />
        ),
      },
      {
        key: "bookedPercentage",
        label: "Booked %",
        align: "right",
        sortable: true,
        definition:
          "Booked remaining divided by aligned Carespaces for this navigator. Already billed Carespaces are excluded from booked remaining.",
        sortValue: (row) =>
          row.alignedCount && row.bookedRemainingCount !== null
            ? row.bookedRemainingCount / row.alignedCount
            : null,
        render: (row) =>
          row.bookedRemainingCount === null
            ? "—"
            : percentage({
                alignedCount: row.alignedCount,
                completedCount: row.bookedRemainingCount,
              }),
      },
      {
        key: "projectedPercentage",
        label: "Projected EOM %",
        align: "right",
        sortable: true,
        definition:
          "DCMP-billed Carespaces plus booked remaining, divided by aligned Carespaces. Includes all billing sources, including Other calls. Assumes upcoming work becomes billable and excludes work not yet booked.",
        sortValue: (row) =>
          row.alignedCount && row.bookedRemainingCount !== null
            ? (row.completedCount + row.bookedRemainingCount) / row.alignedCount
            : null,
        render: projectedPercentage,
      },
    );
  }
  return result;
}

export function CareNavigationMonthlyCompletionSummary(props: {
  rows: CompletionRow[];
  monthStart: string;
  navigators: string[];
  currentMonth?: string;
}) {
  const showProjection =
    props.monthStart.slice(0, 7) === (props.currentMonth ?? todayIsoInTimezone("UTC").slice(0, 7));
  const summary = summarizeMonthlyCompletion(props.rows, props.monthStart, props.navigators);
  return (
    <CarespaceRecordsProvider
      filePrefix="care-navigation"
      records={props.rows.flatMap((row) => row.records ?? [])}
    >
      <KpiGrid>
        <KpiCard
          label="Billed completion"
          value={percentage(summary.current)}
          footnote={
            <>
              <CarespaceCountLink
                count={summary.current.completedCount}
                recordIds={summary.current.recordIds?.completed}
                label="Total CareSpaces with DCMP billing"
              />{" "}
              /{" "}
              <CarespaceCountLink
                count={summary.current.alignedCount}
                recordIds={summary.current.recordIds?.aligned}
                label="Total aligned CareSpaces"
              />{" "}
              aligned during the reporting month
            </>
          }
        />
        {showProjection ? (
          <>
            <KpiCard
              label="Booked remaining"
              value={
                <CarespaceCountLink
                  count={summary.current.bookedRemainingCount}
                  recordIds={summary.current.recordIds?.bookedRemaining}
                  label="Total booked remaining"
                />
              }
              footnote="Upcoming billable appointments and expected low-tier groups, excluding already billed Carespaces"
            />
            <KpiCard
              label="Projected end of month"
              value={projectedPercentage(summary.current)}
              footnote="Billed plus booked remaining / aligned; assumes booked work becomes billable. Excludes work not yet booked."
            />
          </>
        ) : null}
      </KpiGrid>
      <div className="overflow-auto rounded-lg border border-border/70 p-4">
        <h3 className="font-medium">By care navigator</h3>
        <DataTable
          columns={columns("Navigator", showProjection)}
          rows={summary.navigators}
          rowKey={(row) => row.id}
        />
      </div>
      <div className="overflow-auto rounded-lg border border-border/70 p-4">
        <h3 className="font-medium">Month over month</h3>
        <DataTable columns={columns("Month")} rows={summary.months} rowKey={(row) => row.id} />
      </div>
    </CarespaceRecordsProvider>
  );
}

export function CareNavigationMonthlyCompletion(props: {
  anchorDate: string;
  monthStart: string;
  navigatorUserIds: string[];
}) {
  const query = useQuery(careNavigationMonthlyCompletionQueryOptions(props.anchorDate));
  return (
    <section className="space-y-3" aria-label="Total monthly completion">
      <h2 className="text-lg font-semibold">Total monthly completion</h2>
      <p className="text-xs text-muted-foreground">{MONTHLY_COMPLETION_DEFINITION}</p>
      {query.isPending ? (
        <p>Loading monthly completion…</p>
      ) : query.isError ? (
        <p role="alert">Monthly completion is temporarily unavailable.</p>
      ) : (
        <>
          {query.data.cache === "STALE" ? (
            <p role="status">
              Showing previously loaded monthly completion while refresh is unavailable.
            </p>
          ) : null}
          <CareNavigationMonthlyCompletionSummary
            rows={query.data.rows.filter((row) =>
              props.navigatorUserIds.includes(row.navigatorUserId),
            )}
            monthStart={props.monthStart}
            navigators={[]}
          />
        </>
      )}
    </section>
  );
}
